import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@iconify/react';
import type { ProcessFolder } from '@/types/process-folder.types';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useUpdateProcessFolder } from '@/hooks/process-folder/use-process-folder';
import React, { useState, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';

const editProcessFolderSchema = z.object({
  name: z.string().min(1, 'Process folder name is required').max(100, 'Process folder name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
});

type EditProcessFolderFormData = z.infer<typeof editProcessFolderSchema>;

interface EditProcessFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: ProcessFolder | null;
}

export const EditProcessFolderModal: React.FC<EditProcessFolderModalProps> = ({ isOpen, onClose, folder }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateProcessFolderMutation = useUpdateProcessFolder();

  const form = useForm<EditProcessFolderFormData>({
    resolver: zodResolver(editProcessFolderSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  useEffect(() => {
    if (folder) {
      form.reset({
        name: folder.name,
        description: folder.description || '',
      });
    }
  }, [folder, form]);

  const onSubmit = async (data: EditProcessFolderFormData) => {
    if (!folder) return;

    try {
      setIsSubmitting(true);
      await updateProcessFolderMutation.mutateAsync({
        id: folder.id,
        data,
      });
      onClose();
    } catch (error) {
      console.error('Error updating process folder:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="material-symbols:edit-folder" className="text-xl" />
            {t('processManagement.editProcessFolder')}
          </DialogTitle>
          <DialogDescription>
            {t('processManagement.editProcessFolderDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('processManagement.folderName')} *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('processManagement.enterFolderName')}
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('processManagement.folderDescription')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('processManagement.enterFolderDescription')}
                      className="resize-none"
                      rows={3}
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Icon icon="eos-icons:loading" className="mr-2" />
                    {t('common.updating')}
                  </>
                ) : (
                  <>
                    <Icon icon="material-symbols:save" className="mr-2" />
                    {t('common.update')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

